import type { User } from '@loggy/shared';

import { Button, Card, Heading, Inline, Stack, Text } from '../design/index.js';
import { BodyProfileForm } from './BodyProfileForm.js';
import { ShareSettings } from './ShareSettings.js';
import { WithingsCard } from './WithingsCard.js';
import { navigateToDashboard } from './useHashRoute.js';
import styles from './Settings.module.css';

/**
 * Everything that is about the account rather than about the measurements.
 *
 * These lived on the overview until it became a stack of cards with the orbit
 * at the bottom. Connecting a device is something done once and then forgotten;
 * it does not deserve permanent space on the page you open every day.
 */
export function Settings({ user, onSignOut }: { user: User; onSignOut: () => void }) {
  return (
    <Stack gap={8} className={styles.page} data-testid="settings">
      <Stack gap={4}>
        <Button
          variant="ghost"
          size="sm"
          onClick={navigateToDashboard}
          data-testid="settings-back"
          className={styles.back}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M15 6l-6 6 6 6" />
          </svg>
          Orbit
        </Button>
        <Heading level={1} className={styles.title}>Settings</Heading>
      </Stack>

      <Stack gap={4}>
        <Text size="xs" color="muted" className={styles.label}>Connected services</Text>
        <WithingsCard />
      </Stack>

      <ShareSettings />

      <BodyProfileForm />

      <Stack gap={4}>
        <Text size="xs" color="muted" className={styles.label}>Your account</Text>
        <Card>
          <Stack gap={5}>
            <Stack gap={2}>
              <Inline gap={4} wrap>
                <Text size="sm" color="muted" className={styles.key}>Name</Text>
                <Text size="sm">{user.displayName}</Text>
              </Inline>
              <Inline gap={4} wrap>
                <Text size="sm" color="muted" className={styles.key}>Email</Text>
                <Text size="sm">{user.email}</Text>
              </Inline>
              <Inline gap={4} wrap>
                <Text size="sm" color="muted" className={styles.key}>Signed in with</Text>
                <Text size="sm">Google</Text>
              </Inline>
            </Stack>

            <Stack gap={2} className={styles.signOut}>
              <Button variant="secondary" size="sm" onClick={onSignOut} data-testid="settings-sign-out">
                Sign out
              </Button>
              <Text size="xs" color="muted">
                Your measurements stay where they are.
              </Text>
            </Stack>
          </Stack>
        </Card>
      </Stack>
    </Stack>
  );
}
